import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { IFollowUp } from '../../viewModels/leads/interfaces';

/**
 * One follow-up option as surfaced by the per-outcome endpoint. Mirrors the
 * shape `FollowUpSelect` in the legacy queue consumes from
 * `queue.lead.followUpOptions.options[*]` (`aida/components/followup/FollowUpSelect/index.tsx:82-89`):
 * `option.id` is what the save mutation expects (one of `FollowUpOptionIds`),
 * `option.label` is the display string. `isDefault` is set on the option whose
 * `id === IFollowUp.defaultValueId`.
 */
export type FollowUpOption = {
	id: string;
	label: string;
	isDefault?: boolean;
};

export type UseFollowUpOptionsResult = {
	/** Per-outcome options as an array. Empty when no outcome is selected. */
	options: FollowUpOption[];
	/** The id of the option marked default by the server, or `null` when absent. */
	defaultId: string | null;
	/** Whether the entire follow-up control should be hidden (server flag). */
	isDisabled: boolean;
	isLoading: boolean;
	error: Error | null;
};

const QUERY_KEY = 'focused-view/follow-up-options' as const;

/**
 * Per-outcome follow-up options. Mirrors `LeadViewModel.getFollowUp`
 * (`aida/viewModels/leads/lead.ts:317-334`):
 *
 *   GET lead/{companyId}/PhoneCallOutcome/{outcomeId}/FollowUp
 *
 * The legacy queue calls this after every outcome pick to refresh which
 * follow-up shortcuts are valid for that outcome and which one to default to.
 * We expose it as a clean react-query hook — the server response is the only
 * source of truth, so cached results are keyed on `[companyId, outcomeId]`.
 *
 * The hook is disabled until both `companyId` and `outcomeId` are present;
 * callers should fall back to a static list (e.g. `FollowUpOptionIds`) when
 * `options` is empty so the UI still works pre-pick.
 */
export function useFollowUpOptions(
	companyId: string | undefined,
	outcomeId: string | undefined
): UseFollowUpOptionsResult {
	const userSession = useUserSession();

	const query: UseQueryResult<IFollowUp, Error> = useQuery<IFollowUp, Error>({
		queryKey: [QUERY_KEY, companyId, outcomeId],
		enabled: !!companyId && !!outcomeId,
		queryFn: async () => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<IFollowUp>(
				ImpersonationBroker.composeApiUrl({
					urlPath: `lead/${companyId}/PhoneCallOutcome/${outcomeId}/FollowUp`,
				}),
				'GET'
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return result.value;
		},
	});

	const data = query.data;
	const defaultId = data?.defaultValueId ?? null;
	const options: FollowUpOption[] = (data?.options ?? []).map(o => ({
		id: o.id ?? '',
		label: o.label,
		isDefault: !!o.id && o.id === defaultId,
	}));

	return {
		options,
		defaultId,
		isDisabled: !!data?.isDisabled,
		isLoading: query.isLoading,
		error: query.error ?? null,
	};
}
